import clsx from "clsx";
import React, { useEffect, useState } from "react";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import LazyModal from "web/react/components/lazy-modal/lazy-modal";
import styles from "./generic-sticky-modal.module.css";

type StickyModalStyleTypes = "fullscreen" | "sheet";

export interface GenericStickyModalProps {
    children: JSX.Element;
    isOpen: boolean;
    onAfterOpen?: () => void;
    onClose: () => void;
    className?: string;
    overlayClassName?: string;
    closable?: boolean;
    noScroll?: boolean;
    styleType?: StickyModalStyleTypes;
}

function GenericStickyModal({
    children,
    isOpen,
    onAfterOpen,
    onClose,
    className,
    overlayClassName,
    closable = true,
    noScroll,
    styleType = "sheet",
}: GenericStickyModalProps): null | JSX.Element {
    const [animate, setAnimate] = useState(false);
    const [animateOut, setAnimateOut] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setAnimate(false);
            setAnimateOut(false);
        }
    }, [isOpen]);

    let appElement: HTMLElement | undefined;
    if (document.body) {
        appElement = document.body.querySelector<HTMLDivElement>(".site-wrapper") || undefined;
    }

    function closeModal(): void {
        setAnimateOut(true);
        setTimeout(function () {
            onClose();
        }, 200);
    }

    return (
        <LazyModal
            appElement={appElement}
            className={clsx(styles.stickyModal, className, {
                [styles.animateIn]: animate,
                [styles.animateOut]: animateOut,
                [styles.fullscreen]: styleType === "fullscreen",
            })}
            isOpen={isOpen}
            onAfterOpen={(): void => {
                onAfterOpen && onAfterOpen();
                setTimeout(function () {
                    setAnimate(true);
                }, 100);
            }}
            noScroll={noScroll}
            onRequestClose={closeModal}
            overlayClassName={clsx(styles.overlay, overlayClassName)}
            shouldCloseOnOverlayClick={closable}
            shouldCloseOnEsc={closable}
        >
            {closable && <CloseButton className={styles.closeButton} onClick={closeModal} />}
            {children}
        </LazyModal>
    );
}

export default GenericStickyModal;
